import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  assetUrl,
  createListing,
  deleteListingPhoto,
  getCurrentUser,
  getListing,
  getToken,
  setListingCover,
  updateListing,
  uploadListingPhotos,
} from "./api";

const blank = {
  baslik: "",
  aciklama: "",
  fiyat: "",
  adres: "",
  city: "",
  district: "",
  neighborhood: "",
  oda_sayisi: "",
  brut_metrekare: "",
  latitude: "",
  longitude: "",
  instagram_url: "",
  durum: "yayinda",
};

export function ListingFormPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerUserId = Number(searchParams.get("ownerUserId") || 0) || null;
  const ownerLabel = searchParams.get("ownerLabel") || "";
  const [form, setForm] = useState(blank);
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(!!listingId);

  useEffect(() => {
    if (!getToken()) {
      navigate(
        `/giris?next=${listingId ? `/ilan-duzenle/${listingId}` : "/ilan-ver"}`,
        { replace: true },
      );
      return;
    }
    getCurrentUser()
      .then((user) => {
        if (![1, 3].includes(Number(user.rol_id)))
          throw new Error(
            "İlan işlemleri yalnızca onaylı emlak danışmanları içindir.",
          );
        return listingId ? getListing(listingId) : null;
      })
      .then((listing) => {
        if (!listing) return;
        setForm({
          ...blank,
          ...listing,
          city: listing.city || "",
          district: listing.district || "",
          neighborhood: listing.neighborhood || "",
          oda_sayisi:
            listing.ozellikler?.oda_sayisi ||
            listing.listing_features?.["Özellikler.oda_sayisi"] ||
            "",
          brut_metrekare:
            listing.ozellikler?.brut_metrekare ||
            listing.listing_features?.["Özellikler.m²_brut"] ||
            "",
        });
        setExistingPhotos(listing.goruntuler || []);
      })
      .catch((reason) => setError(reason.message))
      .finally(() => setBusy(false));
  }, [listingId, navigate]);

  const change = (event) =>
    setForm({ ...form, [event.target.name]: event.target.value });
  const removePhoto = async (photo) => {
    if (
      !window.confirm(
        "Bu fotoğraf ilan ve sunucudan silinecek. Devam edilsin mi?",
      )
    )
      return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await deleteListingPhoto(listingId, photo.id);
      setExistingPhotos((items) =>
        items.filter((item) => item.id !== photo.id),
      );
      setNotice("Fotoğraf silindi.");
    } catch (reason) {
      setError(reason.message);
    } finally {
      setBusy(false);
    }
  };
  const chooseCover = async (photo) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await setListingCover(listingId, photo.id);
      setExistingPhotos((items) =>
        items.map((item) => ({
          ...item,
          vitrin: item.id === photo.id ? 1 : 0,
        })),
      );
      setNotice("Vitrin fotoğrafı güncellendi.");
    } catch (reason) {
      setError(reason.message);
    } finally {
      setBusy(false);
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...form,
        ...(ownerUserId ? { user_id: ownerUserId, owner_user_id: ownerUserId } : {}),
        konum_lat: form.latitude,
        konum_lng: form.longitude,
        ozellikler: {
          oda_sayisi: form.oda_sayisi,
          brut_metrekare: form.brut_metrekare,
        },
        listing_features: {
          "Özellikler.oda_sayisi": form.oda_sayisi,
          "Özellikler.m²_brut": form.brut_metrekare,
        },
      };
      const result = listingId
        ? await updateListing(listingId, payload)
        : await createListing(payload);
      const savedListingId = listingId || result.ilan_no || result.ilanNo;
      if (photos.length && savedListingId) {
        const uploadResult = await uploadListingPhotos(savedListingId, photos);
        setExistingPhotos((items) => [
          ...items,
          ...(uploadResult.fotograflar || []),
        ]);
        setPhotos([]);
      }
      setNotice(
        listingId ? "İlan ve fotoğraflar güncellendi." : "İlan kaydedildi.",
      );
      if (!listingId)
        setTimeout(() => navigate(`/ilan/${savedListingId}`), 900);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="shell section">
      <Link className="back" to="/profil">
        ← Profilime dön
      </Link>
      <form className="listing-form" onSubmit={submit}>
        <p className="eyebrow">DANIŞMAN PANELİ</p>
        <h1>{listingId ? "İlanı düzenle" : "Yeni ilan ver"}</h1>
        {ownerUserId && <p className="notice"><strong>{ownerLabel || `Kullanıcı #${ownerUserId}`}</strong> adına ilan oluşturuyorsunuz.</p>}
        {error && <p className="error compact">{error}</p>}
        {notice && <p className="notice">{notice}</p>}
        <label>
          Başlık *
          <input required name="baslik" value={form.baslik} onChange={change} />
        </label>
        <label>
          Açıklama
          <textarea
            name="aciklama"
            value={form.aciklama || ""}
            onChange={change}
          />
        </label>
        <div className="form-grid">
          <label>
            Fiyat
            <input
              name="fiyat"
              value={form.fiyat || ""}
              onChange={change}
              placeholder="Örn. 5.000.000 TL"
            />
          </label>
          <label>
            İlan durumu
            <select name="durum" value={form.durum} onChange={change}>
              <option value="yayinda">Yayında</option>
              <option value="taslak">Taslak</option>
            </select>
          </label>
          <label>
            Oda sayısı
            <input
              name="oda_sayisi"
              value={form.oda_sayisi || ""}
              onChange={change}
              placeholder="3+1"
            />
          </label>
          <label>
            Brüt alan (m²)
            <input
              name="brut_metrekare"
              value={form.brut_metrekare || ""}
              onChange={change}
            />
          </label>
        </div>
        <label>
          Adres
          <input name="adres" value={form.adres || ""} onChange={change} />
        </label>
        <div className="form-grid">
          <label>
            Şehir
            <input name="city" value={form.city} onChange={change} />
          </label>
          <label>
            İlçe
            <input name="district" value={form.district} onChange={change} />
          </label>
          <label>
            Mahalle
            <input
              name="neighborhood"
              value={form.neighborhood}
              onChange={change}
            />
          </label>
          <label>
            Enlem (GPS)
            <input
              name="latitude"
              inputMode="decimal"
              value={form.latitude || form.konum_lat || ""}
              onChange={change}
              placeholder="37.759..."
            />
          </label>
          <label>
            Boylam (GPS)
            <input
              name="longitude"
              inputMode="decimal"
              value={form.longitude || form.konum_lng || ""}
              onChange={change}
              placeholder="27.407..."
            />
          </label>
        </div>
        <label>
          Instagram video/Reels bağlantısı
          <input
            type="url"
            name="instagram_url"
            value={form.instagram_url || ""}
            onChange={change}
            placeholder="https://www.instagram.com/reel/..."
          />
          <small>
            Yalnızca herkese açık Instagram video, Reels veya gönderi
            bağlantıları kabul edilir ve ilanın içinde oynatılır.
          </small>
        </label>
        {listingId && (
          <section className="photo-manager">
            <div>
              <h2>Mevcut fotoğraflar</h2>
              <p>
                Vitrin görselini seçebilir veya artık kullanmadığınız fotoğrafı
                silebilirsiniz.
              </p>
            </div>
            {existingPhotos.length ? (
              <div className="photo-manager-grid">
                {existingPhotos.map((photo, index) => (
                  <article
                    key={photo.id}
                    className={
                      photo.vitrin ? "managed-photo cover" : "managed-photo"
                    }
                  >
                    <img
                      src={assetUrl(photo.thumbnail_url || photo.dosya_yolu)}
                      loading="lazy"
                      decoding="async"
                      alt={`İlan fotoğrafı ${index + 1}`}
                    />
                    <div>
                      <strong>
                        {photo.vitrin
                          ? "Vitrin fotoğrafı"
                          : `Fotoğraf ${index + 1}`}
                      </strong>
                      <button
                        type="button"
                        disabled={busy || Boolean(photo.vitrin)}
                        onClick={() => chooseCover(photo)}
                      >
                        Vitrin yap
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={busy}
                        onClick={() => removePhoto(photo)}
                      >
                        Sil
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="empty-media">Bu ilanda henüz fotoğraf yok.</p>
            )}
          </section>
        )}
        <label>
          Yeni ilan fotoğrafları
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(event) =>
              setPhotos(Array.from(event.target.files || []))
            }
          />
          <small>
            JPG, PNG veya WebP seçebilirsiniz. Seçilen dosya: {photos.length}.
            Yeni fotoğraflar mevcut fotoğraflara eklenir.
          </small>
        </label>
        <button disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</button>
      </form>
    </main>
  );
}
